'use client';

import {
  attendantThreesomeFFM,
  attendantThreesomeMMF,
  requestServiceDescription,
  requestServiceDuoDateBisexual,
  requestServiceDuoDateBisexualDescription,
  requestServiceDuoDateHeterosexual,
  requestServiceDuoDateHeterosexualDescription,
  requestServiceFFMDescription,
  requestServiceMMFDescription,
  requestServiceNoCompanion,
  requestServiceTitle,
  wizardNext,
  wizardPrev,
} from '@bells/i18n';
import { Button } from '@bells/ui/components/button';
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@bells/ui/components/dialog';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@bells/ui/components/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';

import { useRequest } from '@/components/girl/request-context';
import { SelectServiceBox } from '@/components/girl/select-service-box';
import {
  AttendantSearchSingleResponse,
  ThreesomeStatus,
} from '@bells/schema/models/attendant';
import { RequestForm, RequestFormSchema } from '@bells/schema/models/request';
import { CircleX, Users, Venus, VenusAndMars } from 'lucide-react';

export const StepChooseService = ({
  data,
  resolve,
  goBack,
}: {
  data: AttendantSearchSingleResponse;
  address?: RequestForm['address'];
  duration?: number;
  resolve?: (resolve: ThreesomeStatus | null | undefined) => void;
  goBack?: () => void;
}) => {
  const request = useRequest();

  const form = useForm<Pick<RequestForm, 'service'>>({
    resolver: zodResolver(RequestFormSchema.pick({ service: true })),
    mode: 'all',
    defaultValues: {
      service: request.values.service ?? null,
    },
  });

  const submit = form.handleSubmit((val) => {
    request.setValues({
      service: val.service ? val.service : undefined,
    });

    if (resolve) {
      resolve(val.service);
    }
  });

  return (
    <>
      <DialogHeader className='p-6 pb-0'>
        <DialogTitle>{requestServiceTitle()}</DialogTitle>
        <DialogDescription>{requestServiceDescription()}</DialogDescription>
      </DialogHeader>
      <div className='flex-1 overflow-y-auto p-6'>
        <FormProvider {...form}>
          <form className='w-full' onSubmit={submit}>
            <FormField
              control={form.control}
              name='service'
              render={({ field }) => (
                <FormItem className={'w-full'}>
                  <FormControl>
                    <div className='grid grid-cols-1 gap-2'>
                      <SelectServiceBox
                        title={requestServiceNoCompanion()}
                        selected={!field.value}
                        icon={CircleX}
                        onSelect={() => field.onChange(null)}
                      ></SelectServiceBox>

                      {data.threesome?.includes(ThreesomeStatus.MMF) &&
                        data.mmfPrice && (
                          <SelectServiceBox
                            title={attendantThreesomeMMF()}
                            icon={Users}
                            selected={field.value == ThreesomeStatus.MMF}
                            onSelect={() => field.onChange(ThreesomeStatus.MMF)}
                            description={requestServiceMMFDescription({
                              price: data.mmfPrice || 0,
                            })}
                          ></SelectServiceBox>
                        )}

                      {data.threesome?.includes(ThreesomeStatus.FFM) &&
                        data.ffmPrice && (
                          <SelectServiceBox
                            title={attendantThreesomeFFM()}
                            icon={Users}
                            selected={field.value == ThreesomeStatus.FFM}
                            onSelect={() => field.onChange(ThreesomeStatus.FFM)}
                            description={requestServiceFFMDescription({
                              price: data.ffmPrice || 0,
                            })}
                          ></SelectServiceBox>
                        )}

                      {data.threesome?.includes(
                        ThreesomeStatus.ESCORT_PARTNER_BISEXUAL
                      ) && (
                        <SelectServiceBox
                          title={requestServiceDuoDateBisexual()}
                          icon={VenusAndMars}
                          selected={
                            field.value ==
                            ThreesomeStatus.ESCORT_PARTNER_BISEXUAL
                          }
                          onSelect={() =>
                            field.onChange(
                              ThreesomeStatus.ESCORT_PARTNER_BISEXUAL
                            )
                          }
                          description={requestServiceDuoDateBisexualDescription()}
                        ></SelectServiceBox>
                      )}

                      {data.threesome?.includes(
                        ThreesomeStatus.ESCORT_PARTNER_HETERO
                      ) && (
                        <SelectServiceBox
                          title={requestServiceDuoDateHeterosexual()}
                          icon={Venus}
                          selected={
                            field.value == ThreesomeStatus.ESCORT_PARTNER_HETERO
                          }
                          onSelect={() =>
                            field.onChange(
                              ThreesomeStatus.ESCORT_PARTNER_HETERO
                            )
                          }
                          description={requestServiceDuoDateHeterosexualDescription()}
                        ></SelectServiceBox>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </FormProvider>
      </div>
      <DialogFooter className='gap-2 p-6 pt-0'>
        <Button type='button' onClick={goBack} variant={'outline'}>
          {wizardPrev()}
        </Button>
        <Button
          type='submit'
          onClick={submit}
          disabled={!form.formState.isValid}
        >
          {wizardNext()}
        </Button>
      </DialogFooter>
    </>
  );
};
